import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import ImageDropzone from "components/ImageDropzone";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeBanner, updateBanner } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  bannerId: string;
}

const initialValues = {
  location: "",
  redirectUrl: "",
  sortOrder: "",
};

const BannerFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  bannerId,
}) => {
  const isAddMode = !bannerId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const [filesDesktopBannerImage, setFilesDesktopBannerImage] = useState<any>(
    []
  );
  const [filesMobileBannerImage, setFilesMobileBannerImage] = useState<any>([]);
  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { banners, isFormLoading } = useSelector(
    (state: any) => state.bannerReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setFilesDesktopBannerImage([]);
    setFilesMobileBannerImage([]);
  };

  const handleSubmit = (values: any) => {
    const { location, redirectUrl, sortOrder } = values;
    const desktopBannerImage =
      typeof filesDesktopBannerImage[0] === "object"
        ? filesDesktopBannerImage[0]
        : null;
    const mobileBannerImage =
      typeof filesMobileBannerImage[0] === "object"
        ? filesMobileBannerImage[0]
        : null;

    isAddMode
      ? dispatch(
          storeBanner(
            {
              desktopBannerImage,
              mobileBannerImage,
              location,
              redirectUrl,
              sortOrder,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateBanner(
            bannerId,
            {
              desktopBannerImage,
              mobileBannerImage,
              location,
              redirectUrl,
              sortOrder,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const bannerDetail = banners.find(
        (banner: any) => banner.id === parseInt(bannerId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("location", bannerDetail.location);
        formikRef.current.setFieldValue(
          "redirectUrl",
          bannerDetail.redirect_url
        );
        formikRef.current.setFieldValue("sortOrder", bannerDetail.sort_order);

        setFilesDesktopBannerImage([
          `${appUrl}/storage/${bannerDetail.desktop_img_url}`,
        ]);
        setFilesMobileBannerImage([
          `${appUrl}/storage/${bannerDetail.mobile_img_url}`,
        ]);
      }
    }
  }, [isAddMode, banners, bannerId, appUrl]);

  const validationSchema = yup.object({
    location: yup.string().required("Location is required field"),
    redirectUrl: yup.string().required("Redirect Url is required field"),
    sortOrder: yup.number().required("Sort Order is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form>
              <DialogHeader>
                <Text>{isAddMode ? "Add Banner" : "Update Banner"}</Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Stack spacing="xs">
                    <FormLabel>Desktop Banner Image</FormLabel>
                    <ImageDropzone
                      files={filesDesktopBannerImage}
                      setFiles={setFilesDesktopBannerImage}
                    />
                  </Stack>
                  <Stack spacing="xs">
                    <FormLabel>Mobile Banner Image</FormLabel>
                    <ImageDropzone
                      files={filesMobileBannerImage}
                      setFiles={setFilesMobileBannerImage}
                    />
                  </Stack>
                  <Field name="location">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="location">Location</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="redirectUrl">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="redirectUrl">
                          Redirect Url
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="sortOrder">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="sortOrder">Sort Order</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="number"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default BannerFormModal;
